import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard,
  Calendar,
  Building2,
  MapPin,
  Users,
  UserCheck,
  Tag,
  FormInput,
  ClipboardList,
  DoorOpen,
  BarChart3,
  Repeat,
  Settings,
  type LucideIcon,
} from 'lucide-react'
import { useAuth } from '@/features/auth/AuthContext'
import type { UserRole } from '@/types/auth'
import { cn } from '@/lib/utils'

interface NavItem {
  key: string
  icon: LucideIcon
  path: string
  labelKey: string
  minRoles: UserRole[]
}

const navigationItems: NavItem[] = [
  {
    key: 'dashboard',
    icon: LayoutDashboard,
    path: '/app',
    labelKey: 'navigation.dashboard',
    minRoles: ['viewer', 'gatehouse', 'operator', 'manager', 'tenant_admin', 'super_admin'],
  },
  {
    key: 'visits',
    icon: Calendar,
    path: '/app/visits',
    labelKey: 'navigation.visits',
    minRoles: ['gatehouse', 'operator', 'manager', 'tenant_admin', 'super_admin'],
  },
  {
    key: 'companies',
    icon: Building2,
    path: '/app/companies',
    labelKey: 'navigation.companies',
    minRoles: ['viewer', 'gatehouse', 'operator', 'manager', 'tenant_admin', 'super_admin'],
  },
  {
    key: 'locations',
    icon: MapPin,
    path: '/app/locations',
    labelKey: 'navigation.locations',
    minRoles: ['viewer', 'gatehouse', 'operator', 'manager', 'tenant_admin', 'super_admin'],
  },
  {
    key: 'hosts',
    icon: UserCheck,
    path: '/app/hosts',
    labelKey: 'navigation.hosts',
    minRoles: ['viewer', 'gatehouse', 'operator', 'manager', 'tenant_admin', 'super_admin'],
  },
  {
    key: 'employees',
    icon: Users,
    path: '/app/employees',
    labelKey: 'navigation.employees',
    minRoles: ['viewer', 'gatehouse', 'operator', 'manager', 'tenant_admin', 'super_admin'],
  },
  {
    key: 'visit-categories',
    icon: Tag,
    path: '/app/visit-categories',
    labelKey: 'navigation.visitCategories',
    minRoles: ['manager', 'tenant_admin', 'super_admin'],
  },
  {
    key: 'custom-fields',
    icon: FormInput,
    path: '/app/custom-fields',
    labelKey: 'navigation.customFields',
    minRoles: ['manager', 'tenant_admin', 'super_admin'],
  },
  {
    key: 'templates',
    icon: Repeat,
    path: '/app/templates',
    labelKey: 'navigation.templates',
    minRoles: ['manager', 'tenant_admin', 'super_admin'],
  },
  {
    key: 'gatehouse',
    icon: DoorOpen,
    path: '/app/gatehouse',
    labelKey: 'navigation.gatehouse',
    minRoles: ['gatehouse', 'operator', 'manager', 'tenant_admin', 'super_admin'],
  },
  {
    key: 'audit-log',
    icon: ClipboardList,
    path: '/app/audit-log',
    labelKey: 'navigation.auditLog',
    minRoles: ['manager', 'tenant_admin', 'super_admin'],
  },
  {
    key: 'reports',
    icon: BarChart3,
    path: '/app/reports',
    labelKey: 'navigation.reports',
    minRoles: ['manager', 'tenant_admin', 'super_admin'],
  },
  {
    key: 'settings',
    icon: Settings,
    path: '/app/settings',
    labelKey: 'navigation.settings',
    minRoles: ['tenant_admin', 'super_admin'],
  },
]

interface AppSidebarProps {
  isOpen: boolean
}

export function AppSidebar({ isOpen }: AppSidebarProps) {
  const { t } = useTranslation()
  const { hasRole } = useAuth()

  const visibleItems = navigationItems.filter((item) => hasRole(item.minRoles))

  return (
    <aside
      className={cn(
        'hidden md:flex flex-col border-r bg-card transition-all duration-200',
        isOpen ? 'w-60' : 'w-16'
      )}
    >
      <div className={cn('flex items-center border-b h-16 px-4', isOpen ? 'justify-start' : 'justify-center')}>
        <span className="text-lg font-bold">{isOpen ? t('app.name') : 'P'}</span>
      </div>
      <nav className="flex-1 p-2 space-y-1">
        {visibleItems.map((item) => (
          <NavLink
            key={item.key}
            to={item.path}
            end={item.path === '/app'}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                !isOpen && 'justify-center px-2'
              )
            }
          >
            <item.icon className="h-5 w-5 shrink-0" />
            {isOpen && <span>{t(item.labelKey)}</span>}
          </NavLink>
        ))}
      </nav>
    </aside>
  )
}
